import type { Appointment } from "fhir/r5";
import UserService from "./UserService";

export const APPOINTMENT_CAPACITY_EXTENSION_URL =
    "https://chunancy.fr/fhir/StructureDefinition/upcare-appointment-capacity";

const USER_IDENTIFIER_SYSTEM = "https://chunancy.fr/fhir/identifier/upcare-user";
const ATTENDEE_CODE = "ATND";
const ACCEPTED_STATUSES = ["accepted", "tentative"];

export interface AppointmentParticipationInfo {
    participantCount: number;
    participantCapacity?: number;
    isRegistered: boolean;
}

export const getAppointmentParticipationInfo = (
    appointment: Appointment
): AppointmentParticipationInfo => {
    const attendees = getAttendees(appointment);
    const participantCount = attendees.filter((participant) =>
        ACCEPTED_STATUSES.includes(participant.status ?? "")
    ).length;
    const explicitCapacity = getExplicitCapacity(appointment);
    const participantCapacity = explicitCapacity ?? (
        attendees.length > participantCount ? attendees.length : undefined
    );

    return {
        participantCount,
        participantCapacity,
        isRegistered: isCurrentUserRegistered(appointment)
    };
};

export const registerCurrentUserToAppointment = async (
    fhirBaseUrl: string,
    appointment: Appointment
): Promise<Appointment> => {
    if (!appointment.id) {
        throw new Error("Impossible d'inscrire un participant sur un Appointment sans identifiant.");
    }

    if (isCurrentUserRegistered(appointment)) {
        return appointment;
    }

    const username = getCurrentUserKey();
    if (!username) {
        throw new Error("Impossible d'identifier l'utilisateur connecté.");
    }

    const participation = getAppointmentParticipationInfo(appointment);
    if (
        participation.participantCapacity !== undefined &&
        participation.participantCount >= participation.participantCapacity
    ) {
        throw new Error("Cette formation est complète.");
    }

    const display = getCurrentUserDisplay() ?? username;
    const updated: Appointment = {
        ...appointment,
        participant: [
            ...(appointment.participant ?? []),
            {
                actor: {
                    identifier: {
                        system: USER_IDENTIFIER_SYSTEM,
                        value: username
                    },
                    display
                },
                type: [{
                    coding: [{
                        system: "http://terminology.hl7.org/CodeSystem/v3-ParticipationType",
                        code: ATTENDEE_CODE,
                        display: "attender"
                    }]
                }],
                status: "accepted"
            }
        ]
    };

    const baseUrl = fhirBaseUrl.replace(/\/$/, "");
    const response = await fetch(`${baseUrl}/Appointment/${appointment.id}`, {
        method: "PUT",
        headers: {
            Accept: "application/fhir+json",
            "Content-Type": "application/fhir+json"
        },
        body: JSON.stringify(updated)
    });

    if (!response.ok) {
        const body = await response.text();
        throw new Error(body || `Erreur FHIR ${response.status}`);
    }

    const persisted = await response.json().catch(() => undefined) as Appointment | undefined;
    return persisted?.resourceType === "Appointment" ? persisted : updated;
};

const getAttendees = (appointment: Appointment) => appointment.participant?.filter((participant) =>
    participant.type?.some((type) =>
        type.coding?.some((coding) => coding.code === ATTENDEE_CODE)
    )
) ?? [];

const getExplicitCapacity = (appointment: Appointment): number | undefined => {
    const extension = appointment.extension?.find((item) =>
        item.url === APPOINTMENT_CAPACITY_EXTENSION_URL ||
        /(?:capacity|max-participants|places)$/i.test(item.url)
    );
    const extensionAny = extension as any;
    const extensionValue = extensionAny?.valuePositiveInt ?? extensionAny?.valueInteger;

    if (typeof extensionValue === "number" && Number.isFinite(extensionValue) && extensionValue > 0) {
        return extensionValue;
    }

    const comment = typeof (appointment as any).comment === "string"
        ? (appointment as any).comment as string
        : "";
    const match = comment.match(/Capacit(?:é|e)\s*:\s*(\d+)/i);
    const commentValue = match ? Number(match[1]) : undefined;

    return commentValue && commentValue > 0 ? commentValue : undefined;
};

const isCurrentUserRegistered = (appointment: Appointment): boolean => {
    const currentUserKey = getCurrentUserKey();
    if (!currentUserKey) return false;

    const currentDisplay = getCurrentUserDisplay()?.trim().toLowerCase();

    return getAttendees(appointment).some((participant) => {
        if (!ACCEPTED_STATUSES.includes(participant.status ?? "")) return false;

        const actorIdentifier = participant.actor?.identifier;
        if (
            actorIdentifier?.system === USER_IDENTIFIER_SYSTEM &&
            actorIdentifier.value === currentUserKey
        ) {
            return true;
        }

        const display = participant.actor?.display?.trim().toLowerCase();
        return !!currentDisplay && display === currentDisplay;
    });
};

const getCurrentUserKey = (): string | undefined => {
    const token = UserService.getTokenParsed() as unknown as Record<string, unknown> | undefined;
    const username = UserService.getUsername();
    const email = typeof token?.email === "string" ? token.email : undefined;
    return username ?? email;
};

const getCurrentUserDisplay = (): string | undefined => {
    const token = UserService.getTokenParsed() as unknown as Record<string, unknown> | undefined;
    if (typeof token?.name === "string") return token.name;
    if (typeof token?.preferred_username === "string") return token.preferred_username;
    return UserService.getUsername();
};
